import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

const CATEGORIES = ["Food & Beverage", "Retail", "Tech", "Services", "Beauty", "Fashion", "Media", "Health", "Education", "Real Estate", "Other"];

export default function SubmitFounder() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [payLoading, setPayLoading] = useState(false);

  const [form, setForm] = useState({
    name: "", age: "", business_name: "", tagline: "", description: "",
    category: "Food & Beverage", city: "", state: "", goal_dollars: "",
    video_url: "", photo_url: "", linkedin: "", instagram: "", website: "",
    email: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startPitchCheckout = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/founder-pitch-checkout", {
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout. Please try again.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed. Try again.");
    } finally {
      setPayLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age) : undefined,
        goal_dollars: parseInt(form.goal_dollars),
      };
      await api.post("/founders", payload);
      toast.success("Application submitted! We'll review and get back to you soon.");
      navigate("/founders");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Auth gate (defensive; ProtectedRoute in App.js already redirects) ----
  if (authLoading || !user || user === false) {
    return <div className="pt-32 text-center text-bdu-cream/60">Loading…</div>;
  }

  // ---- Paywall gate ----
  if (!user.pitch_access_granted) {
    return (
      <div className="pt-24 max-w-3xl mx-auto px-6 sm:px-10 pb-24" data-testid="founder-paywall">
        <div className="label-eyebrow">Founders Circle · Pitch Access</div>
        <h1 className="mt-6 font-serif text-5xl text-bdu-cream tracking-tight">
          Unlock your pitch <span className="italic text-bdu-gold">— join the Founders Circle.</span>
        </h1>
        <p className="mt-6 text-bdu-cream/70 text-lg leading-relaxed">
          Pitching for major funding requires a one-time <span className="text-bdu-gold">$300</span> access
          fee. This unlocks your Black Dollars United membership and pays for our team to properly vet
          your application.
        </p>

        {/* Fee breakdown */}
        <div className="mt-10 border border-bdu-gold/25 bg-bdu-surface p-8" data-testid="pitch-fee-breakdown">
          <div className="label-eyebrow" style={{ fontSize: "0.65rem" }}>What you're paying for</div>

          <div className="mt-6 divide-y divide-bdu-gold/10">
            <div className="flex items-start justify-between py-4">
              <div className="pr-4">
                <div className="flex items-center gap-2 text-bdu-cream">
                  <ShieldCheck size={18} className="text-bdu-gold" />
                  <span className="font-serif text-xl">Membership</span>
                </div>
                <p className="mt-1 text-sm text-bdu-cream/60">
                  Full Black Dollars United member status. Voting rights on community initiatives,
                  member-only events, and early access to future perks.
                </p>
              </div>
              <div className="text-bdu-gold font-serif text-2xl whitespace-nowrap">$100</div>
            </div>

            <div className="flex items-start justify-between py-4">
              <div className="pr-4">
                <div className="flex items-center gap-2 text-bdu-cream">
                  <Sparkles size={18} className="text-bdu-gold" />
                  <span className="font-serif text-xl">Pitch Admin Fee</span>
                </div>
                <p className="mt-1 text-sm text-bdu-cream/60">
                  Covers our review team's time to vet your business plan, verify identity, and
                  publish your pitch to donors.
                  <span className="block mt-1 text-bdu-cream/40 italic">Non-refundable, even if your pitch is not approved.</span>
                </p>
              </div>
              <div className="text-bdu-gold font-serif text-2xl whitespace-nowrap">$200</div>
            </div>

            <div className="flex items-center justify-between py-5">
              <div className="text-bdu-cream font-serif text-xl">Total due today</div>
              <div className="text-bdu-cream font-serif text-3xl">$300</div>
            </div>
          </div>

          <button
            onClick={startPitchCheckout}
            disabled={payLoading}
            className="btn-gold w-full justify-center mt-8"
            data-testid="pitch-pay-btn"
          >
            <Lock size={16} />
            {payLoading ? "Redirecting to secure checkout…" : "Pay $300 & Unlock My Pitch"}
            <ArrowRight size={16} />
          </button>
          <p className="mt-4 text-xs text-bdu-cream/50 text-center">
            Payment is processed securely by Stripe. You'll return here to complete your application.
          </p>
        </div>

        <p className="mt-8 text-xs text-bdu-cream/40 text-center">
          Logged in as <span className="text-bdu-cream/70">{user.email}</span>. Wrong account?{" "}
          <a href="/login" className="underline text-bdu-gold">Switch</a>.
        </p>
      </div>
    );
  }

  // ---- Access granted: show application form ----
  return (
    <div className="pt-24 max-w-3xl mx-auto px-6 sm:px-10 pb-24">
      <div className="label-eyebrow">Founders Circle · Application</div>
      <h1 className="mt-6 font-serif text-5xl text-bdu-cream tracking-tight">
        Pitch your vision to the community.
      </h1>
      <div className="mt-4 inline-flex items-center gap-2 border border-bdu-gold/40 px-3 py-1.5 text-xs uppercase tracking-widest text-bdu-gold" data-testid="pitch-access-badge">
        <ShieldCheck size={14} /> Pitch Access Unlocked
      </div>
      <p className="mt-6 text-bdu-cream/70">
        Tell us about your business idea. Once vetted, your pitch video and funding goal go live
        for our donor community to support.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6" data-testid="founder-submit-form">
        <Field label="Your Name">
          <input required value={form.name} onChange={update("name")} className="bdu-input" data-testid="founder-name-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Age (optional)">
            <input type="number" min="13" max="99" value={form.age} onChange={update("age")} className="bdu-input" data-testid="founder-age-input" />
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={update("category")} className="bdu-input" data-testid="founder-category-input">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Business Name">
          <input required value={form.business_name} onChange={update("business_name")} className="bdu-input" data-testid="founder-business-input" />
        </Field>
        <Field label="Tagline (one sentence)">
          <input value={form.tagline} onChange={update("tagline")} className="bdu-input" placeholder="Reinventing how the neighborhood eats." data-testid="founder-tagline-input" />
        </Field>
        <Field label="Your Story / Business Plan">
          <textarea required minLength={20} rows={6} value={form.description} onChange={update("description")} className="bdu-input" placeholder="Tell us who you are, what you're building, why it matters, and how you'll use the money." data-testid="founder-description-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="City"><input required value={form.city} onChange={update("city")} className="bdu-input" data-testid="founder-city-input" /></Field>
          <Field label="State"><input required value={form.state} onChange={update("state")} className="bdu-input" data-testid="founder-state-input" /></Field>
        </div>
        <Field label="Funding Goal (USD)">
          <input required type="number" min="1" value={form.goal_dollars} onChange={update("goal_dollars")} className="bdu-input" placeholder="25000" data-testid="founder-goal-input" />
        </Field>
        <Field label="Pitch Video URL (YouTube link OR direct .mp4 URL)">
          <input value={form.video_url} onChange={update("video_url")} className="bdu-input" placeholder="https://youtube.com/watch?v=..." data-testid="founder-video-input" />
        </Field>
        <Field label="Photo/Logo URL (optional)">
          <input value={form.photo_url} onChange={update("photo_url")} className="bdu-input" placeholder="https://..." data-testid="founder-photo-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="LinkedIn (optional)"><input value={form.linkedin} onChange={update("linkedin")} className="bdu-input" data-testid="founder-linkedin-input" /></Field>
          <Field label="Instagram (optional)"><input value={form.instagram} onChange={update("instagram")} className="bdu-input" data-testid="founder-instagram-input" /></Field>
        </div>
        <Field label="Website (optional)"><input value={form.website} onChange={update("website")} className="bdu-input" data-testid="founder-website-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Email"><input type="email" value={form.email} onChange={update("email")} className="bdu-input" data-testid="founder-email-input" /></Field>
          <Field label="Phone (optional)"><input value={form.phone} onChange={update("phone")} className="bdu-input" data-testid="founder-phone-input" /></Field>
        </div>

        <button type="submit" disabled={submitting} className="btn-gold w-full justify-center mt-8" data-testid="founder-submit-btn">
          {submitting ? "Submitting..." : "Submit My Pitch"}
        </button>
        <p className="text-xs text-bdu-cream/50 text-center">
          Our team reviews applications within 5-7 business days. You'll hear back via email.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      {children}
    </label>
  );
}
